import { a as triggers, i as exports$1, n as defineWorker, o as bindings, r as defineContainer, t as defineSettings } from "./public-BYCVISMH.mjs";
import * as nodeModule from "node:module";
import * as z from "zod";
import { fileURLToPath, pathToFileURL } from "node:url";
import { AsyncLocalStorage } from "node:async_hooks";

//#region rolldown:runtime
var __getOwnPropNames = Object.getOwnPropertyNames;
var __commonJS = (cb, mod) => function() {
	return mod || (0, cb[__getOwnPropNames(cb)[0]])((mod = { exports: {} }).exports, mod), mod.exports;
};

//#endregion
//#region src/schema.ts
const RemoteBindingDevSchema = z.strictObject({ remote: z.boolean().optional() });
const AssetsSchema = z.strictObject({
	htmlHandling: z.enum([
		"auto-trailing-slash",
		"drop-trailing-slash",
		"force-trailing-slash",
		"none"
	]).optional(),
	notFoundHandling: z.enum([
		"single-page-application",
		"404-page",
		"none"
	]).optional(),
	runWorkerFirst: z.union([z.array(z.string()), z.boolean()]).optional()
});
const BrowserBindingSchema = z.strictObject({
	type: z.literal("browser"),
	dev: RemoteBindingDevSchema.optional()
});
const WorkerBindingSchema = z.strictObject({
	type: z.literal("worker"),
	worker: z.string(),
	exportName: z.string().optional(),
	props: z.record(z.string(), z.unknown()).optional(),
	dev: RemoteBindingDevSchema.optional()
});
const D1BindingSchema = z.strictObject({
	type: z.literal("d1"),
	name: z.string().optional(),
	id: z.string().optional(),
	dev: RemoteBindingDevSchema.optional()
});
const KVBindingSchema = z.strictObject({
	type: z.literal("kv"),
	id: z.string().optional(),
	dev: RemoteBindingDevSchema.optional()
});
const QueueBindingSchema = z.strictObject({
	type: z.literal("queue"),
	name: z.string().optional(),
	deliveryDelay: z.number().optional(),
	dev: RemoteBindingDevSchema.optional()
});
const R2BindingSchema = z.strictObject({
	type: z.literal("r2"),
	name: z.string().optional(),
	jurisdiction: z.string().optional(),
	dev: RemoteBindingDevSchema.extend({ experimentalS3Credentials: z.object({
		accessKeyId: z.string(),
		secretAccessKey: z.string()
	}).optional() }).optional()
});
const AnalyticsEngineDatasetBindingSchema = z.strictObject({
	type: z.literal("analytics-engine-dataset"),
	name: z.string().optional()
});
const FlagshipBindingSchema = z.strictObject({
	type: z.literal("flagship"),
	id: z.string().optional(),
	dev: RemoteBindingDevSchema.optional()
});
const HyperdriveBindingSchema = z.strictObject({
	type: z.literal("hyperdrive"),
	id: z.string(),
	dev: z.strictObject({ connectionString: z.string().optional() }).optional()
});
const KnownBindingSchema = z.discriminatedUnion("type", [
	z.strictObject({
		type: z.literal("agent-memory"),
		namespace: z.string(),
		dev: RemoteBindingDevSchema.optional()
	}),
	z.strictObject({
		type: z.literal("ai"),
		dev: RemoteBindingDevSchema.optional()
	}),
	z.strictObject({
		type: z.literal("ai-search"),
		name: z.string(),
		dev: RemoteBindingDevSchema.optional()
	}),
	z.strictObject({
		type: z.literal("ai-search-namespace"),
		namespace: z.string(),
		dev: RemoteBindingDevSchema.optional()
	}),
	AnalyticsEngineDatasetBindingSchema,
	z.strictObject({
		type: z.literal("artifacts"),
		namespace: z.string(),
		dev: RemoteBindingDevSchema.optional()
	}),
	z.strictObject({ type: z.literal("assets") }),
	BrowserBindingSchema,
	D1BindingSchema,
	z.strictObject({
		type: z.literal("dispatch-namespace"),
		namespace: z.string().optional(),
		outbound: z.strictObject({
			worker: z.string(),
			parameters: z.array(z.string()).optional()
		}).optional(),
		dev: RemoteBindingDevSchema.optional()
	}),
	z.strictObject({
		type: z.literal("durable-object"),
		worker: z.string(),
		exportName: z.string()
	}),
	FlagshipBindingSchema,
	HyperdriveBindingSchema,
	z.strictObject({
		type: z.literal("images"),
		dev: RemoteBindingDevSchema.optional()
	}),
	z.strictObject({
		type: z.literal("json"),
		value: z.json()
	}),
	KVBindingSchema,
	z.strictObject({
		type: z.literal("logfwdr"),
		destination: z.string()
	}),
	z.strictObject({
		type: z.literal("media"),
		dev: RemoteBindingDevSchema.optional()
	}),
	z.strictObject({
		type: z.literal("mtls-certificate"),
		id: z.string(),
		dev: RemoteBindingDevSchema.optional()
	}),
	z.strictObject({
		type: z.literal("pipeline"),
		name: z.string(),
		dev: RemoteBindingDevSchema.optional()
	}),
	QueueBindingSchema,
	z.strictObject({
		type: z.literal("rate-limit"),
		namespace: z.string(),
		simple: z.strictObject({
			limit: z.number(),
			period: z.union([z.literal(10), z.literal(60)])
		})
	}),
	R2BindingSchema,
	z.strictObject({ type: z.literal("secret") }),
	z.strictObject({
		type: z.literal("secrets-store-secret"),
		storeId: z.string(),
		secretName: z.string()
	}),
	z.strictObject({
		type: z.literal("send-email"),
		destinationAddress: z.string().optional(),
		allowedDestinationAddresses: z.array(z.string()).optional(),
		allowedSenderAddresses: z.array(z.string()).optional(),
		dev: RemoteBindingDevSchema.optional()
	}).refine((value) => value.destinationAddress === void 0 || value.allowedDestinationAddresses === void 0, { message: "\"send-email\" bindings cannot specify both \"destinationAddress\" and \"allowedDestinationAddresses\"" }),
	z.strictObject({
		type: z.literal("stream"),
		dev: RemoteBindingDevSchema.optional()
	}),
	z.strictObject({
		type: z.literal("text"),
		value: z.string()
	}),
	z.strictObject({
		type: z.literal("vectorize"),
		name: z.string(),
		dev: RemoteBindingDevSchema.optional()
	}),
	z.strictObject({ type: z.literal("version-metadata") }),
	z.strictObject({
		type: z.literal("vpc-service"),
		id: z.string(),
		dev: RemoteBindingDevSchema.optional()
	}),
	z.strictObject({
		type: z.literal("vpc-network"),
		tunnelId: z.string().optional(),
		networkId: z.string().optional(),
		dev: RemoteBindingDevSchema.optional()
	}).refine((value) => value.tunnelId !== void 0 !== (value.networkId !== void 0), { error: ({ input }) => {
		const value = input;
		return value.tunnelId !== void 0 && value.networkId !== void 0 ? `"vpc-network" bindings must specify exactly one of "tunnelId" or "networkId", not both` : `"vpc-network" bindings must specify either "tunnelId" or "networkId"`;
	} }),
	z.strictObject({
		type: z.literal("web-search"),
		dev: RemoteBindingDevSchema.optional()
	}),
	WorkerBindingSchema,
	z.strictObject({ type: z.literal("worker-loader") })
]);
const UnsafeBindingSchema = z.looseObject({
	type: z.templateLiteral(["unsafe:", z.string().min(1)]),
	dev: z.strictObject({
		plugin: z.strictObject({
			package: z.string(),
			name: z.string()
		}),
		options: z.record(z.string(), z.unknown()).optional()
	}).optional()
});
const BindingSchema = z.unknown().transform((value, ctx) => {
	const result = (typeof value === "object" && value !== null && "type" in value && typeof value.type === "string" && value.type.startsWith("unsafe:") ? UnsafeBindingSchema : KnownBindingSchema).safeParse(value);
	if (!result.success) {
		ctx.issues.push(...result.error.issues);
		return z.NEVER;
	}
	return result.data;
});
function isParsedUnsafeBinding(binding) {
	return binding.type.startsWith("unsafe:");
}
const CacheSchema = z.strictObject({
	enabled: z.boolean(),
	crossVersionCache: z.boolean().optional()
});
/**
* Binding types that can only be defined once per worker.
*/
const SINGLETON_BINDING_TYPES = new Set([
	"ai",
	"assets",
	"browser",
	"images",
	"media",
	"stream",
	"version-metadata",
	"web-search"
]);
const listFormatter = new Intl.ListFormat("en-US");
function validateSingletonBindings(env, ctx) {
	const seen = /* @__PURE__ */ new Set();
	const duplicates = /* @__PURE__ */ new Set();
	for (const binding of Object.values(env)) {
		const type = binding.type;
		if (SINGLETON_BINDING_TYPES.has(type)) {
			if (seen.has(type)) duplicates.add(type);
			seen.add(type);
		}
	}
	if (duplicates.size > 0) ctx.addIssue({
		code: "custom",
		message: `${listFormatter.format([...duplicates].sort())} bindings can only be defined once`
	});
}
const EnvSchema = z.record(z.string(), BindingSchema).superRefine(validateSingletonBindings).optional();
const ContainerImageDockerfileSchema = z.strictObject({
	dockerfile: z.string().min(1),
	buildContext: z.string().optional(),
	buildVars: z.record(z.string(), z.string()).optional()
});
const ContainerImageReferenceSchema = z.strictObject({ reference: z.string().min(1) });
const ContainerImageLocalReferenceSchema = z.strictObject({ localReference: z.string().min(1) });
const InputContainerImageSchema = z.union([ContainerImageDockerfileSchema, ContainerImageReferenceSchema]);
const OutputContainerImageSchema = z.union([ContainerImageReferenceSchema, ContainerImageLocalReferenceSchema]);
const VALID_ROLLOUT_STEP_PERCENTAGES = new Set([
	5,
	10,
	20,
	25,
	50,
	100
]);
const ContainerRolloutStepPercentageSchema = z.union([z.number(), z.array(z.number())]).superRefine((stepPercentage, ctx) => {
	if (typeof stepPercentage === "number") {
		if (!VALID_ROLLOUT_STEP_PERCENTAGES.has(stepPercentage)) ctx.addIssue({
			code: "custom",
			message: "A rollout step percentage must be one of 5, 10, 20, 25, 50, or 100"
		});
		return;
	}
	if (stepPercentage.length === 0) {
		ctx.addIssue({
			code: "custom",
			message: "A rollout must contain at least one step percentage"
		});
		return;
	}
	for (const [index, step] of stepPercentage.entries()) {
		if (step < 10 || step > 100) ctx.addIssue({
			code: "custom",
			path: [index],
			message: "Rollout step percentages must be between 10 and 100"
		});
		const previousStep = stepPercentage[index - 1];
		if (previousStep !== void 0 && step < previousStep) ctx.addIssue({
			code: "custom",
			path: [index],
			message: "Rollout step percentages must be in ascending order"
		});
	}
	const lastIndex = stepPercentage.length - 1;
	if (stepPercentage[lastIndex] !== 100) ctx.addIssue({
		code: "custom",
		path: [lastIndex],
		message: "The final rollout step percentage must be 100"
	});
});
function validateContainerRelationships(container, ctx) {
	const stepPercentage = container.rollout?.stepPercentage;
	if (Array.isArray(stepPercentage) && container.maxInstances !== void 0 && stepPercentage.length > container.maxInstances) ctx.addIssue({
		code: "custom",
		path: ["rollout", "stepPercentage"],
		message: "A rollout cannot contain more steps than the maximum number of instances"
	});
}
const ContainerObservabilityBaseSchema = z.strictObject({
	enabled: z.boolean().optional(),
	logs: z.strictObject({ enabled: z.boolean().optional() }).optional()
});
const ContainerObservabilitySchema = z.union([ContainerObservabilityBaseSchema.extend({ targetInstancePercentage: z.number().min(0).max(100).optional() }), ContainerObservabilityBaseSchema.extend({ targetInstanceCount: z.number().int().nonnegative().optional() })]);
const BaseContainerSchema = z.strictObject({
	type: z.literal("container"),
	name: z.string().min(1),
	observability: ContainerObservabilitySchema.optional(),
	unsafe: z.record(z.string(), z.unknown()).optional()
});
const StandardContainerBaseSchema = BaseContainerSchema.extend({
	maxInstances: z.number().int().nonnegative().default(20),
	instanceType: z.union([z.enum([
		"basic",
		"lite",
		"standard-1",
		"standard-2",
		"standard-3",
		"standard-4"
	]), z.strictObject({
		vcpu: z.number().min(.0625).optional(),
		memoryMib: z.number().nonnegative().optional(),
		diskMb: z.number().nonnegative().optional()
	})]).optional(),
	schedulingPolicy: z.enum(["default", "regional"]).optional(),
	ssh: z.strictObject({
		enabled: z.boolean(),
		port: z.number().int().min(1).max(65535).optional()
	}).optional(),
	authorizedKeys: z.array(z.strictObject({
		name: z.string(),
		publicKey: z.string()
	})).optional(),
	constraints: z.strictObject({
		regions: z.array(z.enum([
			"ENAM",
			"WNAM",
			"EEUR",
			"WEUR",
			"APAC",
			"SAM",
			"ME",
			"OC",
			"AFR"
		])).optional(),
		jurisdiction: z.enum(["eu", "fedramp"]).optional()
	}).optional(),
	rollout: z.strictObject({
		kind: z.enum([
			"full-auto",
			"none",
			"full-manual"
		]).optional(),
		stepPercentage: ContainerRolloutStepPercentageSchema.optional(),
		activeGracePeriod: z.number().nonnegative().optional()
	}).optional()
});
const DurableObjectContainerBaseSchema = BaseContainerSchema.extend({ schedulingPolicy: z.literal("durable-object") });
/**
* Input Container schema — validates user-authored `cloudflare.config.ts`
* Container exports. Dockerfiles are built by the consuming build tool.
*/
const InputContainerSchema = z.union([StandardContainerBaseSchema.extend({ image: InputContainerImageSchema }).superRefine(validateContainerRelationships), DurableObjectContainerBaseSchema.extend({ images: z.record(z.string(), InputContainerImageSchema).optional() })]);
/**
* Output Container schema — validates Container configs in the Build Output
* Specification, after any Dockerfile has been built into a local or remote
* image reference.
*/
const OutputContainerSchema = z.union([StandardContainerBaseSchema.extend({ image: OutputContainerImageSchema }).superRefine(validateContainerRelationships), DurableObjectContainerBaseSchema.extend({ images: z.record(z.string(), OutputContainerImageSchema).optional() })]);
const DurableObjectCreatedExportSchema = z.strictObject({
	type: z.literal("durable-object"),
	state: z.literal("created").optional(),
	storage: z.enum(["sqlite", "legacy-kv"]),
	container: z.string().optional()
});
const DurableObjectDeletedExportSchema = z.strictObject({
	type: z.literal("durable-object"),
	state: z.literal("deleted")
});
const DurableObjectRenamedExportSchema = z.strictObject({
	type: z.literal("durable-object"),
	state: z.literal("renamed"),
	renamedTo: z.string()
});
const DurableObjectTransferredExportSchema = z.strictObject({
	type: z.literal("durable-object"),
	state: z.literal("transferred"),
	transferredTo: z.string()
});
const DurableObjectExpectingTransferExportSchema = z.strictObject({
	type: z.literal("durable-object"),
	state: z.literal("expecting-transfer"),
	storage: z.enum(["sqlite", "legacy-kv"]),
	transferFrom: z.string(),
	container: z.string().optional()
});
const WorkerEntrypointExportSchema = z.strictObject({
	type: z.literal("worker"),
	cache: z.strictObject({ enabled: z.boolean() }).optional()
});
const DurableObjectCreatedSqliteExportSchema = DurableObjectCreatedExportSchema.extend({ storage: z.literal("sqlite") });
const DurableObjectCreatedLegacyKvExportSchema = DurableObjectCreatedExportSchema.omit({ container: true }).extend({ storage: z.literal("legacy-kv") });
const DurableObjectExpectingTransferSqliteExportSchema = DurableObjectExpectingTransferExportSchema.extend({ storage: z.literal("sqlite") });
const DurableObjectExpectingTransferLegacyKvExportSchema = DurableObjectExpectingTransferExportSchema.omit({ container: true }).extend({ storage: z.literal("legacy-kv") });
const ExportSchema = z.union([
	DurableObjectCreatedSqliteExportSchema,
	DurableObjectCreatedLegacyKvExportSchema,
	DurableObjectDeletedExportSchema,
	DurableObjectRenamedExportSchema,
	DurableObjectTransferredExportSchema,
	DurableObjectExpectingTransferSqliteExportSchema,
	DurableObjectExpectingTransferLegacyKvExportSchema,
	WorkerEntrypointExportSchema
]);
const LimitsSchema = z.strictObject({
	cpuMs: z.number().optional(),
	subrequests: z.number().optional()
});
const ObservabilitySchema = z.strictObject({
	enabled: z.boolean().optional(),
	headSamplingRate: z.number().optional(),
	redactQueryString: z.boolean().optional(),
	logs: z.strictObject({
		enabled: z.boolean().optional(),
		headSamplingRate: z.number().optional(),
		invocationLogs: z.boolean().optional(),
		persist: z.boolean().optional(),
		destinations: z.array(z.string()).optional()
	}).optional(),
	traces: z.strictObject({
		enabled: z.boolean().optional(),
		headSamplingRate: z.number().optional(),
		persist: z.boolean().optional(),
		destinations: z.array(z.string()).optional()
	}).optional()
});
const PlacementSchema = z.union([
	z.strictObject({
		mode: z.enum(["off", "smart"]),
		hint: z.string().optional()
	}),
	z.strictObject({
		mode: z.literal("targeted").optional(),
		region: z.string()
	}),
	z.strictObject({
		mode: z.literal("targeted").optional(),
		host: z.string()
	}),
	z.strictObject({
		mode: z.literal("targeted").optional(),
		hostname: z.string()
	})
]);
const TailConsumerSchema = z.strictObject({
	worker: z.string(),
	streaming: z.boolean().optional()
});
const TriggerSchema = z.discriminatedUnion("type", [
	z.strictObject({
		type: z.literal("email"),
		addresses: z.array(z.string())
	}),
	z.strictObject({
		type: z.literal("fetch"),
		pattern: z.string(),
		zone: z.string().optional()
	}),
	z.strictObject({
		type: z.literal("queue"),
		name: z.string(),
		deadLetterQueue: z.string().optional(),
		maxBatchSize: z.number().optional(),
		maxBatchTimeout: z.number().optional(),
		maxConcurrency: z.number().nullable().optional(),
		maxRetries: z.number().optional(),
		retryDelay: z.number().optional(),
		visibilityTimeoutMs: z.number().optional()
	}),
	z.strictObject({
		type: z.literal("scheduled"),
		schedule: z.string()
	}),
	z.strictObject({
		type: z.literal("connect"),
		protocol: z.enum(["tcp"]),
		port: z.number(),
		address: z.string().optional()
	})
]);
const UnsafeSchema = z.strictObject({
	metadata: z.record(z.string(), z.unknown()).optional(),
	capnp: z.union([z.strictObject({
		basePath: z.string(),
		sourceSchemas: z.array(z.string()),
		compiledSchema: z.never().optional()
	}), z.strictObject({
		basePath: z.never().optional(),
		sourceSchemas: z.never().optional(),
		compiledSchema: z.string()
	})]).optional()
});
/**
* Base Worker schema — the set of fields shared between the input
* (user-authored) and output (on-disk) Worker configs.
*/
const BaseWorkerSchema = z.strictObject({
	type: z.literal("worker"),
	name: z.string(),
	compatibilityDate: z.string(),
	compatibilityFlags: z.array(z.string()).optional(),
	assets: AssetsSchema.optional(),
	domains: z.array(z.string()).optional(),
	triggers: z.array(TriggerSchema).optional(),
	tailConsumers: z.array(TailConsumerSchema).optional(),
	cache: CacheSchema.optional(),
	placement: PlacementSchema.optional(),
	limits: LimitsSchema.optional(),
	logpush: z.boolean().optional(),
	observability: ObservabilitySchema.optional(),
	workersDev: z.boolean().optional(),
	previewUrls: z.boolean().optional(),
	firstPartyWorker: z.boolean().optional(),
	unsafe: UnsafeSchema.optional(),
	env: EnvSchema,
	exports: z.record(z.string(), ExportSchema).optional()
});
/**
* Input Worker schema — the shape that user-authored `cloudflare.config.ts`
* files are validated against. Adds an optional `entrypoint` field to the
* base schema.
*/
const InputWorkerSchema = BaseWorkerSchema.extend({ entrypoint: z.union([z.string(), z.strictObject({ default: z.string() })]).transform((value) => typeof value === "string" ? value : value.default).optional() });
/**
* Input settings schema — validates the named `settings` export of a
* `cloudflare.config.ts`. Holds account/deployment settings shared by the other exports.
*/
const InputSettingsSchema = z.strictObject({
	type: z.literal("settings"),
	accountId: z.string().optional(),
	complianceRegion: z.enum(["public", "fedramp-high"]).optional()
});
/**
* Output settings schema — the shape of the top-level `config.json` in the
* Build Output Specification. Adds the `mode` the build was produced in.
*/
const OutputSettingsSchema = InputSettingsSchema.extend({ mode: z.string().optional() });
const DEFAULT_EXPORT_NAME = "default";
const SETTINGS_EXPORT_NAME = "settings";
const SUPPORTED_EXPORT_TYPES = new Set([
	"container",
	"worker",
	"settings"
]);
function invalidConfigExportMessage(exportName) {
	return `The \`${exportName}\` export is not a supported export type. Move constants, helper functions, and other unsupported exports to a separate module.`;
}
const ConfigExportsTypeSchema = z.record(z.string(), z.unknown()).check((ctx) => {
	for (const [key, value] of Object.entries(ctx.value)) {
		const isObject = typeof value === "object" && value !== null;
		const type = isObject && "type" in value ? value.type : void 0;
		if (typeof type !== "string" || !SUPPORTED_EXPORT_TYPES.has(type)) {
			ctx.issues.push({
				code: "custom",
				input: value,
				path: isObject ? [key, "type"] : [key],
				message: invalidConfigExportMessage(key)
			});
			continue;
		}
		if (key === DEFAULT_EXPORT_NAME && type !== "worker") {
			ctx.issues.push({
				code: "custom",
				input: value,
				path: [key],
				message: `The \`${DEFAULT_EXPORT_NAME}\` export is reserved for a \`worker\` config; found a \`${type}\` config.`
			});
			continue;
		}
		const isSettingsName = key === SETTINGS_EXPORT_NAME;
		const isSettingsType = type === "settings";
		if (isSettingsType && !isSettingsName) ctx.issues.push({
			code: "custom",
			input: value,
			path: [key],
			message: `A \`settings\` config is only allowed on the \`${SETTINGS_EXPORT_NAME}\` export; found one on the \`${key}\` export.`
		});
		else if (isSettingsName && !isSettingsType) ctx.issues.push({
			code: "custom",
			input: value,
			path: [key],
			message: `The \`${SETTINGS_EXPORT_NAME}\` export is reserved for a \`settings\` config; found a \`${type}\` config.`
		});
	}
});
const ModuleTypeSchema = z.enum([
	"esm",
	"cjs",
	"python",
	"python-requirement",
	"wasm",
	"text",
	"data",
	"json",
	"sourcemap"
]);
const ManifestModulesSchema = z.record(z.string(), z.strictObject({ type: ModuleTypeSchema }));
const ManifestSchema = z.strictObject({
	type: z.enum(["partial", "complete"]),
	mainModule: z.string(),
	modules: ManifestModulesSchema
}).superRefine((manifest, ctx) => {
	const mainModuleEntry = manifest.modules[manifest.mainModule];
	if (manifest.type === "complete" && mainModuleEntry === void 0) ctx.addIssue({
		code: "custom",
		path: ["modules", manifest.mainModule],
		message: "A complete manifest must include its main module."
	});
	if (manifest.type === "partial" && mainModuleEntry !== void 0) ctx.addIssue({
		code: "custom",
		path: ["modules", manifest.mainModule],
		message: "A partial manifest must not include its main module."
	});
});
/**
* Output Worker schema — the shape of the Worker's `config.json` in the
* Build Output Specification. Adds an optional `manifest` field to the
* base schema.
*/
const OutputWorkerSchema = BaseWorkerSchema.extend({ manifest: ManifestSchema.optional() });

//#endregion
//#region ../../node_modules/.pnpm/ts-dedent@2.2.0/node_modules/ts-dedent/dist/index.js
var require_dist = /* @__PURE__ */ __commonJS({ "../../node_modules/.pnpm/ts-dedent@2.2.0/node_modules/ts-dedent/dist/index.js": ((exports) => {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.dedent = void 0;
	function dedent$1(templ) {
		var values = [];
		for (var _i = 1; _i < arguments.length; _i++) values[_i - 1] = arguments[_i];
		var strings = Array.from(typeof templ === "string" ? [templ] : templ);
		strings[strings.length - 1] = strings[strings.length - 1].replace(/\r?\n([\t ]*)$/, "");
		var indentLengths = strings.reduce(function(arr, str) {
			var matches = str.match(/\n([\t ]+|(?!\s).)/g);
			if (matches) return arr.concat(matches.map(function(match) {
				var _a, _b;
				return (_b = (_a = match.match(/[\t ]/g)) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
			}));
			return arr;
		}, []);
		if (indentLengths.length) {
			var pattern_1 = new RegExp("\n[	 ]{" + Math.min.apply(Math, indentLengths) + "}", "g");
			strings = strings.map(function(str) {
				return str.replace(pattern_1, "\n");
			});
		}
		strings[0] = strings[0].replace(/^\r?\n/, "");
		var string = strings[0];
		values.forEach(function(value, i) {
			var endentations = string.match(/(?:^|\n)( *)$/);
			var endentation = endentations ? endentations[1] : "";
			var indentedValue = value;
			if (typeof value === "string" && value.includes("\n")) indentedValue = String(value).split("\n").map(function(str, i$1) {
				return i$1 === 0 ? str : "" + endentation + str;
			}).join("\n");
			string += indentedValue + strings[i + 1];
		});
		return string;
	}
	exports.dedent = dedent$1;
}) });

//#endregion
//#region src/generate.ts
var import_dist = require_dist();
/**
* Generate the content for worker-configuration.d.ts
*
* @example
* ```typescript
* import { generateTypes } from "@cloudflare/config";
* import { writeFileSync } from "node:fs";
*
* const content = generateTypes({
*   configPath: "./cloudflare.config.ts",
* });
*
* writeFileSync("worker-configuration.d.ts", content);
* ```
*/
function generateTypes({ configPath, packageName = "@cloudflare/config" }) {
	return import_dist.dedent`
		/* eslint-disable */
		// Generated by @cloudflare/config
		type __WorkerConfig = import("${packageName}").UnwrapConfig<typeof import("${configPath.replace(/\.(ts|js|mts|mjs)$/, "")}").default>;
		type __Env = import("${packageName}").InferEnv<__WorkerConfig>;

		declare namespace Cloudflare {
			interface GlobalProps {
				mainModule: import("${packageName}").InferMainModule<__WorkerConfig>;
				durableNamespaces: import("${packageName}").InferDurableNamespaces<__WorkerConfig>;
			}
			interface Env extends __Env {}
		}
		interface Env extends Cloudflare.Env {}

	`;
}

//#endregion
//#region ../workers-utils/dist/chunk-Q72B4Q5Z.mjs
var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", {
	value,
	configurable: true
});

//#endregion
//#region ../workers-utils/dist/chunk-WO2IAW6M.mjs
var UserError = class extends Error {
	static {
		__name(this, "UserError");
	}
	telemetryMessage;
	constructor(message, options) {
		super(message, options);
		Object.setPrototypeOf(this, new.target.prototype);
		this.telemetryMessage = typeof options?.telemetryMessage === "string" ? options.telemetryMessage : options?.telemetryMessage ? message : void 0;
	}
};
var DeprecationError = class extends UserError {
	static {
		__name(this, "DeprecationError");
	}
	constructor(message, options) {
		super(`Deprecation:
${message}`, options);
	}
};
var FatalError = class extends UserError {
	static {
		__name(this, "FatalError");
	}
	code;
	constructor(message, options) {
		super(message, options);
		this.code = options.code;
	}
};
var CommandLineArgsError = class extends UserError {
	static {
		__name(this, "CommandLineArgsError");
	}
};
var JsonFriendlyFatalError = class extends FatalError {
	static {
		__name(this, "JsonFriendlyFatalError");
	}
};
var MissingConfigError = class extends Error {
	static {
		__name(this, "MissingConfigError");
	}
	telemetryMessage;
	constructor(key) {
		super(`Missing config value for ${key}`);
		this.telemetryMessage = `Missing config value for ${key}`;
	}
};
function createFatalError(message, isJson, options) {
	if (isJson) return new JsonFriendlyFatalError(JSON.stringify(message), options);
	return new FatalError(`${message}`, options);
}
__name(createFatalError, "createFatalError");

//#endregion
//#region src/convert.ts
const ROLLOUT_KIND_MAP = {
	"full-auto": "full_auto",
	"full-manual": "full_manual",
	none: "none"
};
/**
* Convert a parsed `@cloudflare/config` config into a Wrangler `RawConfig`.
*
* The caller is responsible for unwrapping any function/promise wrappers and
* validating the configs against their corresponding input schemas before
* passing them in.
*
* @param workerConfig The parsed (post-validation) Worker config.
* @param settingsConfig The optional parsed settings config, whose fields
* are merged onto the result.
* @param containerExports The parsed Container exports to include in the
* result.
* @returns The corresponding Wrangler `RawConfig`.
*/
function convertToWranglerConfig(workerConfig, settingsConfig, containerExports = []) {
	const result = {};
	convertTopLevel(workerConfig, result);
	convertBindingsAndAssets(workerConfig, result);
	convertExports(workerConfig, result);
	convertDomains(workerConfig, result);
	convertTriggers(workerConfig, result);
	convertTailConsumers(workerConfig, result);
	if (settingsConfig !== void 0) convertSettings(settingsConfig, result);
	if (containerExports.length > 0) result.containers = containerExports.map((container) => convertContainer(container));
	return result;
}
function convertContainer(container) {
	if (container.schedulingPolicy === "durable-object") throw new UserError("Durable Object-managed Containers are not currently supported by `convertToWranglerConfig()`.", { telemetryMessage: false });
	const converted = { name: container.name };
	if (container.observability !== void 0) converted.observability = convertContainerObservability(container.observability);
	if (container.unsafe !== void 0) converted.unsafe = container.unsafe;
	converted.image = "dockerfile" in container.image ? container.image.dockerfile : container.image.reference;
	converted.max_instances = container.maxInstances;
	if ("dockerfile" in container.image) {
		if (container.image.buildContext !== void 0) converted.image_build_context = container.image.buildContext;
		if (container.image.buildVars !== void 0) converted.image_vars = container.image.buildVars;
	}
	if (container.instanceType !== void 0) if (typeof container.instanceType === "string") converted.instance_type = container.instanceType;
	else {
		const instanceType = {};
		if (container.instanceType.vcpu !== void 0) instanceType.vcpu = container.instanceType.vcpu;
		if (container.instanceType.memoryMib !== void 0) instanceType.memory_mib = container.instanceType.memoryMib;
		if (container.instanceType.diskMb !== void 0) instanceType.disk_mb = container.instanceType.diskMb;
		converted.instance_type = instanceType;
	}
	if (container.schedulingPolicy !== void 0) converted.scheduling_policy = container.schedulingPolicy;
	if (container.ssh !== void 0) converted.ssh = container.ssh;
	if (container.authorizedKeys !== void 0) converted.authorized_keys = container.authorizedKeys.map(({ name, publicKey }) => ({
		name,
		public_key: publicKey
	}));
	if (container.constraints !== void 0) converted.constraints = container.constraints;
	if (container.rollout !== void 0) {
		if (container.rollout.kind !== void 0) converted.rollout_kind = ROLLOUT_KIND_MAP[container.rollout.kind];
		if (container.rollout.stepPercentage !== void 0) converted.rollout_step_percentage = container.rollout.stepPercentage;
		if (container.rollout.activeGracePeriod !== void 0) converted.rollout_active_grace_period = container.rollout.activeGracePeriod;
	}
	return converted;
}
function convertContainerObservability(observability) {
	const converted = {};
	if (observability.enabled !== void 0) converted.enabled = observability.enabled;
	if (observability.logs !== void 0) converted.logs = observability.logs;
	if ("targetInstancePercentage" in observability && observability.targetInstancePercentage !== void 0) converted.target_instance_percentage = observability.targetInstancePercentage;
	if ("targetInstanceCount" in observability && observability.targetInstanceCount !== void 0) converted.target_instance_count = observability.targetInstanceCount;
	return converted;
}
/**
* Merge a parsed settings config's fields (`account_id`, `compliance_region`)
* onto an existing Wrangler `RawConfig`.
*/
function convertSettings(settings, result) {
	if (settings.accountId !== void 0) result.account_id = settings.accountId;
	if (settings.complianceRegion !== void 0) result.compliance_region = settings.complianceRegion === "fedramp-high" ? "fedramp_high" : "public";
}
function convertTopLevel(config, result) {
	if (config.name !== void 0) result.name = config.name;
	if (typeof config.entrypoint === "string") result.main = config.entrypoint;
	if (config.compatibilityDate !== void 0) result.compatibility_date = config.compatibilityDate;
	if (config.compatibilityFlags !== void 0) result.compatibility_flags = config.compatibilityFlags;
	if (config.workersDev !== void 0) result.workers_dev = config.workersDev;
	if (config.previewUrls !== void 0) result.preview_urls = config.previewUrls;
	if (config.logpush !== void 0) result.logpush = config.logpush;
	if (config.firstPartyWorker !== void 0) result.first_party_worker = config.firstPartyWorker;
	if (config.placement !== void 0) result.placement = config.placement;
	if (config.limits !== void 0) {
		const limits = {};
		if (config.limits.cpuMs !== void 0) limits.cpu_ms = config.limits.cpuMs;
		if (config.limits.subrequests !== void 0) limits.subrequests = config.limits.subrequests;
		result.limits = limits;
	}
	if (config.observability !== void 0) result.observability = convertObservability(config.observability);
	if (config.cache !== void 0) {
		const cache = { enabled: config.cache.enabled };
		if (config.cache.crossVersionCache !== void 0) cache.cross_version_cache = config.cache.crossVersionCache;
		result.cache = cache;
	}
	if (config.unsafe !== void 0) result.unsafe = convertUnsafeTopLevel(config.unsafe);
}
function convertObservability(observability) {
	const out = {};
	if (observability.enabled !== void 0) out.enabled = observability.enabled;
	if (observability.headSamplingRate !== void 0) out.head_sampling_rate = observability.headSamplingRate;
	if (observability.redactQueryString !== void 0) out.redact_query_string = observability.redactQueryString;
	if (observability.logs !== void 0) {
		const logs = {};
		const { logs: src } = observability;
		if (src.enabled !== void 0) logs.enabled = src.enabled;
		if (src.headSamplingRate !== void 0) logs.head_sampling_rate = src.headSamplingRate;
		if (src.invocationLogs !== void 0) logs.invocation_logs = src.invocationLogs;
		if (src.persist !== void 0) logs.persist = src.persist;
		if (src.destinations !== void 0) logs.destinations = src.destinations;
		out.logs = logs;
	}
	if (observability.traces !== void 0) {
		const traces = {};
		const { traces: src } = observability;
		if (src.enabled !== void 0) traces.enabled = src.enabled;
		if (src.headSamplingRate !== void 0) traces.head_sampling_rate = src.headSamplingRate;
		if (src.persist !== void 0) traces.persist = src.persist;
		if (src.destinations !== void 0) traces.destinations = src.destinations;
		out.traces = traces;
	}
	return out;
}
function convertUnsafeTopLevel(unsafe) {
	const out = {};
	if (unsafe.metadata !== void 0) out.metadata = unsafe.metadata;
	if (unsafe.capnp !== void 0) {
		if ("compiledSchema" in unsafe.capnp && unsafe.capnp.compiledSchema) out.capnp = { compiled_schema: unsafe.capnp.compiledSchema };
		else if ("basePath" in unsafe.capnp && unsafe.capnp.basePath) out.capnp = {
			base_path: unsafe.capnp.basePath,
			source_schemas: unsafe.capnp.sourceSchemas ?? []
		};
	}
	return out;
}
function convertBindingsAndAssets(config, result) {
	let assetsBindingName;
	const env = config.env ?? {};
	const kvNamespaces = [];
	const d1Databases = [];
	const r2Buckets = [];
	const vectorize = [];
	const mtlsCertificates = [];
	const hyperdrive = [];
	const pipelines = [];
	const flagship = [];
	const aiSearch = [];
	const aiSearchNamespaces = [];
	const agentMemory = [];
	const analyticsEngineDatasets = [];
	const artifacts = [];
	const dispatchNamespaces = [];
	const secretsStoreSecrets = [];
	const sendEmail = [];
	const vpcServices = [];
	const vpcNetworks = [];
	const workerLoaders = [];
	const ratelimits = [];
	const services = [];
	const durableObjectBindings = [];
	const workflows = [];
	const queueProducers = [];
	const logfwdrBindings = [];
	const unsafeBindings = [];
	const vars = {};
	const secretsRequired = [];
	for (const [name, binding] of Object.entries(env)) {
		if (isParsedUnsafeBinding(binding)) {
			unsafeBindings.push({
				...binding,
				name,
				type: binding.type.slice(7)
			});
			continue;
		}
		switch (binding.type) {
			case "agent-memory":
				agentMemory.push(omitUndefined({
					binding: name,
					namespace: binding.namespace,
					remote: binding.dev?.remote
				}));
				break;
			case "ai":
				result.ai = omitUndefined({
					binding: name,
					remote: binding.dev?.remote
				});
				break;
			case "ai-search":
				aiSearch.push(omitUndefined({
					binding: name,
					instance_name: binding.name,
					remote: binding.dev?.remote
				}));
				break;
			case "ai-search-namespace":
				aiSearchNamespaces.push(omitUndefined({
					binding: name,
					namespace: binding.namespace,
					remote: binding.dev?.remote
				}));
				break;
			case "analytics-engine-dataset":
				analyticsEngineDatasets.push(omitUndefined({
					binding: name,
					dataset: binding.name
				}));
				break;
			case "artifacts":
				artifacts.push(omitUndefined({
					binding: name,
					namespace: binding.namespace,
					remote: binding.dev?.remote
				}));
				break;
			case "assets":
				assetsBindingName = name;
				break;
			case "browser":
				result.browser = omitUndefined({
					binding: name,
					remote: binding.dev?.remote
				});
				break;
			case "d1":
				d1Databases.push(omitUndefined({
					binding: name,
					database_id: binding.id,
					database_name: binding.name,
					remote: binding.dev?.remote
				}));
				break;
			case "dispatch-namespace": {
				const entry = {
					binding: name,
					namespace: binding.namespace
				};
				if (binding.outbound) entry.outbound = omitUndefined({
					service: binding.outbound.worker,
					parameters: binding.outbound.parameters
				});
				if (binding.dev?.remote !== void 0) entry.remote = binding.dev.remote;
				dispatchNamespaces.push(entry);
				break;
			}
			case "durable-object":
				durableObjectBindings.push({
					name,
					class_name: binding.exportName,
					script_name: binding.worker
				});
				break;
			case "flagship":
				flagship.push(omitUndefined({
					binding: name,
					app_id: binding.id,
					remote: binding.dev?.remote
				}));
				break;
			case "hyperdrive":
				hyperdrive.push(omitUndefined({
					binding: name,
					id: binding.id,
					localConnectionString: binding.dev?.connectionString
				}));
				break;
			case "images":
				result.images = omitUndefined({
					binding: name,
					remote: binding.dev?.remote
				});
				break;
			case "json":
				vars[name] = binding.value;
				break;
			case "kv":
				kvNamespaces.push(omitUndefined({
					binding: name,
					id: binding.id,
					remote: binding.dev?.remote
				}));
				break;
			case "logfwdr":
				logfwdrBindings.push({
					name,
					destination: binding.destination
				});
				break;
			case "media":
				result.media = omitUndefined({
					binding: name,
					remote: binding.dev?.remote
				});
				break;
			case "mtls-certificate":
				mtlsCertificates.push(omitUndefined({
					binding: name,
					certificate_id: binding.id,
					remote: binding.dev?.remote
				}));
				break;
			case "pipeline":
				pipelines.push(omitUndefined({
					binding: name,
					stream: binding.name,
					remote: binding.dev?.remote
				}));
				break;
			case "queue":
				queueProducers.push(omitUndefined({
					binding: name,
					queue: binding.name,
					delivery_delay: binding.deliveryDelay,
					remote: binding.dev?.remote
				}));
				break;
			case "rate-limit":
				ratelimits.push({
					name,
					namespace_id: binding.namespace,
					simple: binding.simple
				});
				break;
			case "r2": {
				const experimentalS3Credentials = binding.dev?.experimentalS3Credentials;
				r2Buckets.push(omitUndefined({
					binding: name,
					bucket_name: binding.name,
					jurisdiction: binding.jurisdiction,
					remote: binding.dev?.remote,
					local_dev: experimentalS3Credentials === void 0 ? void 0 : { experimental_s3_credentials: experimentalS3Credentials }
				}));
				break;
			}
			case "secret":
				secretsRequired.push(name);
				break;
			case "secrets-store-secret":
				secretsStoreSecrets.push({
					binding: name,
					store_id: binding.storeId,
					secret_name: binding.secretName
				});
				break;
			case "send-email":
				sendEmail.push(omitUndefined({
					name,
					destination_address: binding.destinationAddress,
					allowed_destination_addresses: binding.allowedDestinationAddresses,
					allowed_sender_addresses: binding.allowedSenderAddresses,
					remote: binding.dev?.remote
				}));
				break;
			case "stream":
				result.stream = omitUndefined({
					binding: name,
					remote: binding.dev?.remote
				});
				break;
			case "text":
				vars[name] = binding.value;
				break;
			case "vectorize":
				vectorize.push(omitUndefined({
					binding: name,
					index_name: binding.name,
					remote: binding.dev?.remote
				}));
				break;
			case "version-metadata":
				result.version_metadata = { binding: name };
				break;
			case "vpc-service":
				vpcServices.push(omitUndefined({
					binding: name,
					service_id: binding.id,
					remote: binding.dev?.remote
				}));
				break;
			case "vpc-network":
				if (binding.tunnelId !== void 0) vpcNetworks.push(omitUndefined({
					binding: name,
					tunnel_id: binding.tunnelId,
					remote: binding.dev?.remote
				}));
				else if (binding.networkId !== void 0) vpcNetworks.push(omitUndefined({
					binding: name,
					network_id: binding.networkId,
					remote: binding.dev?.remote
				}));
				break;
			case "web-search":
				result.websearch = omitUndefined({
					binding: name,
					remote: binding.dev?.remote
				});
				break;
			case "worker":
				services.push(omitUndefined({
					binding: name,
					service: binding.worker,
					entrypoint: binding.exportName,
					props: binding.props,
					remote: binding.dev?.remote
				}));
				break;
			case "worker-loader":
				workerLoaders.push({ binding: name });
				break;
		}
	}
	if (kvNamespaces.length) result.kv_namespaces = kvNamespaces;
	if (d1Databases.length) result.d1_databases = d1Databases;
	if (r2Buckets.length) result.r2_buckets = r2Buckets;
	if (vectorize.length) result.vectorize = vectorize;
	if (mtlsCertificates.length) result.mtls_certificates = mtlsCertificates;
	if (hyperdrive.length) result.hyperdrive = hyperdrive;
	if (pipelines.length) result.pipelines = pipelines;
	if (flagship.length) result.flagship = flagship;
	if (aiSearch.length) result.ai_search = aiSearch;
	if (aiSearchNamespaces.length) result.ai_search_namespaces = aiSearchNamespaces;
	if (agentMemory.length) result.agent_memory = agentMemory;
	if (analyticsEngineDatasets.length) result.analytics_engine_datasets = analyticsEngineDatasets;
	if (artifacts.length) result.artifacts = artifacts;
	if (dispatchNamespaces.length) result.dispatch_namespaces = dispatchNamespaces;
	if (secretsStoreSecrets.length) result.secrets_store_secrets = secretsStoreSecrets;
	if (sendEmail.length) result.send_email = sendEmail;
	if (vpcServices.length) result.vpc_services = vpcServices;
	if (vpcNetworks.length) result.vpc_networks = vpcNetworks;
	if (workerLoaders.length) result.worker_loaders = workerLoaders;
	if (ratelimits.length) result.ratelimits = ratelimits;
	if (services.length) result.services = services;
	if (durableObjectBindings.length) result.durable_objects = { bindings: durableObjectBindings };
	if (workflows.length) result.workflows = workflows;
	if (queueProducers.length) result.queues = {
		...result.queues ?? {},
		producers: queueProducers
	};
	if (logfwdrBindings.length) result.logfwdr = { bindings: logfwdrBindings };
	if (unsafeBindings.length) result.unsafe = {
		...result.unsafe ?? {},
		bindings: unsafeBindings
	};
	if (Object.keys(vars).length) result.vars = vars;
	if (secretsRequired.length) result.secrets = { required: secretsRequired };
	if (config.assets !== void 0 || assetsBindingName !== void 0) {
		const assets = {};
		if (assetsBindingName !== void 0) assets.binding = assetsBindingName;
		if (config.assets?.htmlHandling !== void 0) assets.html_handling = config.assets.htmlHandling;
		if (config.assets?.notFoundHandling !== void 0) assets.not_found_handling = config.assets.notFoundHandling;
		if (config.assets?.runWorkerFirst !== void 0) assets.run_worker_first = config.assets.runWorkerFirst;
		result.assets = assets;
	}
}
function convertExports(config, result) {
	const exports$2 = config.exports;
	if (!exports$2) return;
	const converted = {};
	const unknownExports = {};
	for (const [exportName, value] of Object.entries(exports$2)) {
		if (value.type === "worker") {
			converted[exportName] = value;
			continue;
		}
		if (value.type !== "durable-object") {
			unknownExports[exportName] = value;
			continue;
		}
		switch (value.state) {
			case void 0:
			case "created":
				converted[exportName] = {
					type: "durable-object",
					storage: value.storage,
					...value.storage === "sqlite" && value.container !== void 0 && { container: value.container }
				};
				break;
			case "deleted":
				converted[exportName] = {
					type: "durable-object",
					state: "deleted"
				};
				break;
			case "renamed":
				converted[exportName] = {
					type: "durable-object",
					state: "renamed",
					renamed_to: value.renamedTo
				};
				break;
			case "transferred":
				converted[exportName] = {
					type: "durable-object",
					state: "transferred",
					transferred_to: value.transferredTo
				};
				break;
			case "expecting-transfer":
				converted[exportName] = {
					type: "durable-object",
					state: "expecting-transfer",
					storage: value.storage,
					transfer_from: value.transferFrom,
					...value.storage === "sqlite" && value.container !== void 0 && { container: value.container }
				};
				break;
		}
	}
	if (Object.keys(unknownExports).length > 0) throw new UserError("Unknown export types found: " + Object.entries(unknownExports).map(([exportName, { type }]) => `- ${exportName} : ${type}`).join("\n"), { telemetryMessage: "Unknown export types found" });
	if (Object.keys(converted).length > 0) result.exports = converted;
}
function convertTriggers(config, result) {
	const triggers$1 = config.triggers;
	if (!triggers$1 || triggers$1.length === 0) return;
	const crons = [];
	const routes = result.routes ? [...result.routes] : [];
	const queueConsumers = result.queues?.consumers ? [...result.queues.consumers] : [];
	const connectHandlers = result.connect ? [...result.connect] : [];
	let addresses;
	for (const trigger of triggers$1) switch (trigger.type) {
		case "email":
			addresses ??= [];
			addresses.push(...trigger.addresses);
			break;
		case "scheduled":
			crons.push(trigger.schedule);
			break;
		case "fetch":
			if (trigger.zone === void 0) routes.push(trigger.pattern);
			else if (trigger.zone.includes(".")) routes.push({
				pattern: trigger.pattern,
				zone_name: trigger.zone
			});
			else routes.push({
				pattern: trigger.pattern,
				zone_id: trigger.zone
			});
			break;
		case "queue":
			queueConsumers.push(omitUndefined({
				queue: trigger.name,
				dead_letter_queue: trigger.deadLetterQueue,
				max_batch_size: trigger.maxBatchSize,
				max_batch_timeout: trigger.maxBatchTimeout,
				max_concurrency: trigger.maxConcurrency,
				max_retries: trigger.maxRetries,
				retry_delay: trigger.retryDelay,
				visibility_timeout_ms: trigger.visibilityTimeoutMs
			}));
			break;
		case "connect":
			connectHandlers.push(omitUndefined({
				protocol: trigger.protocol,
				port: trigger.port,
				address: trigger.address
			}));
			break;
	}
	if (crons.length) result.triggers = { crons };
	if (routes.length) result.routes = routes;
	if (queueConsumers.length) result.queues = {
		...result.queues ?? {},
		consumers: queueConsumers
	};
	if (connectHandlers.length) result.connect = connectHandlers;
	if (addresses !== void 0) result.addresses = addresses;
}
function convertDomains(config, result) {
	if (!config.domains || config.domains.length === 0) return;
	const routes = result.routes ? [...result.routes] : [];
	for (const domain of config.domains) routes.push({
		pattern: domain,
		custom_domain: true
	});
	result.routes = routes;
}
function convertTailConsumers(config, result) {
	const consumers = config.tailConsumers;
	if (!consumers || consumers.length === 0) return;
	const tail = [];
	const streaming = [];
	for (const consumer of consumers) if (consumer.streaming) streaming.push({ service: consumer.worker });
	else tail.push({ service: consumer.worker });
	if (tail.length) result.tail_consumers = tail;
	if (streaming.length) result.streaming_tail_consumers = streaming;
}
/**
* Strip keys whose value is `undefined`. Returns a new object preserving the
* input's value type.
*/
function omitUndefined(obj) {
	const out = {};
	for (const [key, value] of Object.entries(obj)) if (value !== void 0) out[key] = value;
	return out;
}

//#endregion
//#region src/load.ts
/**
* @module
*
* The no-cache import logic in this file is adapted from `import-without-cache`
* by Kevin Deng (https://github.com/sxzz/import-without-cache), published under
* the MIT License and Copyright © 2025-PRESENT Kevin Deng.
*
* See https://github.com/sxzz/import-without-cache for the original code.
*/
const CF_WORKER_SCHEME = "cf-worker:";
const CF_WORKER_TYPE = "cf-worker";
const CF_ATTR = "cf";
const CF_NO_CACHE_VALUE = "no-cache";
const CF_NO_CACHE_QUERY_KEY = "cf-no-cache";
const RE_NODE_MODULES = /[/\\]node_modules[/\\]/;
const depsStore = new AsyncLocalStorage();
/**
* Dynamically import a worker config file from disk.
*
* Returns all of the module's exports (keyed by export name, including
* `default`), plus the set of file paths imported during resolution. Callers
* are responsible for unwrapping function/promise/definition wrappers around
* each export and validating them.
*
* @param configPath Filesystem path to the config file. Relative paths are
*   resolved against `process.cwd()`.
* @param options.include When provided, only exports whose names are listed
*   survive into the returned `exports` map. Filtering happens before any
*   resolution or validation.
*/
async function loadConfig(configPath, options) {
	registerConfigHooks();
	const url = pathToFileURL(configPath).href;
	const dependencies = /* @__PURE__ */ new Set();
	const mod = await depsStore.run(dependencies, () => import(url, { with: { [CF_ATTR]: CF_NO_CACHE_VALUE } }));
	const exports$2 = {};
	for (const [name, value] of Object.entries(mod)) {
		if (options?.include && !options.include.includes(name)) continue;
		exports$2[name] = value;
	}
	return {
		exports: exports$2,
		dependencies
	};
}
let deregister;
/**
* Register Node module hooks for loading Worker configs. Idempotent —
* repeated calls return the same deregister function.
*
*  - Handles `with { type: "cf-worker" }` import attributes by synthesising an
*    ES module whose default export is the entrypoint specifier. Relative
*    specifiers (`./`, `../`) are anchored to the importing module and emitted
*    as absolute paths; bare specifiers and virtual-module specifiers pass
*    through unchanged so consumers can apply their own resolution semantics.
*    The referenced module is NOT loaded, is NOT executed, and is NOT added to
*    the dependencies set.
*  - Handles `with { cf: "no-cache" }` import attributes (set internally by
*    `loadConfig`) by tagging the resolved URL with a unique UUID query
*    string so Node treats each import as a fresh module. The UUID is
*    propagated to all transitive imports via the parent URL, ensuring the
*    entire subgraph is re-evaluated. Imports inside `node_modules` are
*    skipped (treated as immutable for config purposes).
*/
function registerConfigHooks() {
	if (deregister) return deregister;
	if (typeof process !== "undefined" && process.versions.bun) throw new Error("cloudflare.config.ts loading is not supported on Bun. Please use Node.js v22.18.0 or higher.");
	const registerHooks = nodeModule.registerHooks;
	if (typeof registerHooks !== "function") throw new Error("cloudflare.config.ts loading requires Node.js v22.18.0 or higher.");
	const hooks = registerHooks({
		resolve(specifier, context, nextResolve) {
			const importAttributes = context.importAttributes ?? {};
			if (importAttributes.type === CF_WORKER_TYPE) {
				const entrypoint = (specifier.startsWith("./") || specifier.startsWith("../")) && context.parentURL ? fileURLToPath(new URL(specifier, context.parentURL)) : specifier;
				return {
					url: `${CF_WORKER_SCHEME}${encodeURIComponent(entrypoint)}`,
					format: "module",
					shortCircuit: true
				};
			}
			const isNoCache = importAttributes[CF_ATTR] === CF_NO_CACHE_VALUE;
			const resolved = nextResolve(specifier, context);
			if (RE_NODE_MODULES.test(resolved.url)) return resolved;
			if (!resolved.url.startsWith("file://")) return resolved;
			const parentUuid = getParentUUID(context.parentURL);
			if (!parentUuid && !isNoCache) return resolved;
			depsStore.getStore()?.add(fileURLToPath(resolved.url));
			resolved.url = appendUUID(resolved.url, parentUuid || crypto.randomUUID());
			return resolved;
		},
		load(url, context, nextLoad) {
			if (url.startsWith(CF_WORKER_SCHEME)) {
				const specifier = decodeURIComponent(url.slice(10));
				return {
					format: "module",
					source: `export default ${JSON.stringify(specifier)};`,
					shortCircuit: true
				};
			}
			cleanupImportAttributes(context);
			return nextLoad(url, context);
		}
	});
	deregister = () => {
		hooks.deregister();
		deregister = void 0;
	};
	return deregister;
}
function getParentUUID(parentURL) {
	if (!parentURL) return;
	return new URL(parentURL).searchParams.get(CF_NO_CACHE_QUERY_KEY) ?? void 0;
}
function appendUUID(url, uuid) {
	const parsed = new URL(url);
	parsed.searchParams.set(CF_NO_CACHE_QUERY_KEY, uuid);
	return parsed.toString();
}
function cleanupImportAttributes(context) {
	if (!context.importAttributes?.[CF_ATTR]) return;
	const attrs = Object.assign(Object.create(null), context.importAttributes);
	delete attrs[CF_ATTR];
	context.importAttributes = attrs;
	Object.freeze(context.importAttributes);
}

//#endregion
//#region src/config-loader.ts
const CROSS_WORKER_BINDING_TYPES = new Set([
	"durable-object",
	"worker",
	"workflow"
]);
function isRecord(value) {
	return typeof value === "object" && value !== null;
}
function isConfigReference(value) {
	return typeof value === "function" || isRecord(value);
}
function normalizeConfigReference(reference, resolved, expectedType) {
	return isRecord(resolved) && resolved.type === expectedType && typeof resolved.name === "string" ? resolved.name : reference;
}
function createDefinitionResolver(ctx) {
	const resolvedDefinitions = /* @__PURE__ */ new Map();
	return (input) => {
		const cached = resolvedDefinitions.get(input);
		if (cached) return cached;
		const resolved = (async () => {
			return await (typeof input === "function" ? input(ctx) : input);
		})();
		resolvedDefinitions.set(input, resolved);
		return resolved;
	};
}
async function normalizeWorkerReferences(resolved, resolveDefinition) {
	if (!isRecord(resolved) || !isRecord(resolved.env)) return resolved;
	const env = { ...resolved.env };
	for (const [bindingName, binding] of Object.entries(env)) {
		if (!isRecord(binding) || typeof binding.type !== "string" || !CROSS_WORKER_BINDING_TYPES.has(binding.type) || !isConfigReference(binding.worker)) continue;
		const target = await resolveDefinition(binding.worker);
		env[bindingName] = {
			...binding,
			worker: normalizeConfigReference(binding.worker, target, "worker")
		};
	}
	return {
		...resolved,
		env
	};
}
async function normalizeContainerReferences(resolved, resolveDefinition, resolvedExports) {
	const issues = [];
	if (!isRecord(resolved) || !isRecord(resolved.exports)) return {
		value: resolved,
		issues
	};
	const workerExports = { ...resolved.exports };
	for (const [exportName, workerExport] of Object.entries(workerExports)) {
		if (!isRecord(workerExport) || workerExport.type !== "durable-object" || workerExport.container === void 0) continue;
		const reference = workerExport.container;
		if (typeof reference === "string") {
			issues.push({
				code: "custom",
				input: reference,
				path: [
					"exports",
					exportName,
					"container"
				],
				message: "Container provided as a string. Reference an exported Container definition instead."
			});
			workerExports[exportName] = {
				...workerExport,
				container: void 0
			};
			continue;
		}
		const target = await resolveDefinition(reference);
		if (isRecord(target) && target.type === "container" && typeof target.name === "string" && !Object.values(resolvedExports).includes(target)) {
			issues.push({
				code: "custom",
				input: reference,
				path: [
					"exports",
					exportName,
					"container"
				],
				message: `The referenced Container "${target.name}" is not exported.`
			});
			workerExports[exportName] = {
				...workerExport,
				container: void 0
			};
			continue;
		}
		workerExports[exportName] = {
			...workerExport,
			container: normalizeConfigReference(reference, target, "container")
		};
	}
	return {
		value: {
			...resolved,
			exports: workerExports
		},
		issues
	};
}
async function normalizeConfigReferences(resolved, resolveDefinition, resolvedExports) {
	return normalizeContainerReferences(await normalizeWorkerReferences(resolved, resolveDefinition), resolveDefinition, resolvedExports);
}
function prefixIssues(issues, prefix) {
	return issues.map((issue) => ({
		...issue,
		path: [prefix, ...issue.path]
	}));
}
function validateUniqueResourceNames(configExports) {
	const issues = [];
	const exportNameByResourceName = {
		container: /* @__PURE__ */ new Map(),
		worker: /* @__PURE__ */ new Map()
	};
	for (const [exportName, config] of Object.entries(configExports)) {
		if (config.type !== "container" && config.type !== "worker") continue;
		const resourceType = config.type === "container" ? "Container" : "Worker";
		const exportNames = exportNameByResourceName[config.type];
		const previousExportName = exportNames.get(config.name);
		if (previousExportName !== void 0) {
			issues.push({
				code: "custom",
				input: config.name,
				path: [exportName, "name"],
				message: `The ${resourceType} name "${config.name}" is also used by the "${previousExportName}" export. ${resourceType} names must be unique.`
			});
			continue;
		}
		exportNames.set(config.name, exportName);
	}
	return issues;
}
function validateContainerExportLinks(configExports) {
	const issues = [];
	const containerNames = /* @__PURE__ */ new Set();
	for (const config of Object.values(configExports)) if (config.type === "container") containerNames.add(config.name);
	const firstReferenceByContainerName = /* @__PURE__ */ new Map();
	for (const [workerExportName, config] of Object.entries(configExports)) {
		if (config.type !== "worker") continue;
		for (const [durableObjectExportName, workerExport] of Object.entries(config.exports ?? {})) {
			if (workerExport.type !== "durable-object" || !("container" in workerExport) || workerExport.container === void 0) continue;
			const containerName = workerExport.container;
			const path = [
				workerExportName,
				"exports",
				durableObjectExportName,
				"container"
			];
			if (!containerNames.has(containerName)) {
				issues.push({
					code: "custom",
					input: containerName,
					path,
					message: `The Container "${containerName}" is not exported from this configuration.`
				});
				continue;
			}
			const firstReference = firstReferenceByContainerName.get(containerName);
			if (firstReference !== void 0) {
				issues.push({
					code: "custom",
					input: containerName,
					path,
					message: `The Container "${containerName}" is already referenced by "${firstReference.workerExportName}.exports.${firstReference.durableObjectExportName}". A Container can only be linked to one Durable Object.`
				});
				continue;
			}
			firstReferenceByContainerName.set(containerName, {
				workerExportName,
				durableObjectExportName
			});
		}
	}
	return issues;
}
/**
* Resolve and validate loaded `cloudflare.config.ts` exports.
*
* Config inputs are resolved once by identity. Top-level Worker and Container
* exports are also parsed once by identity. References are resolved only far
* enough to replace them with resource names, then Container links are
* validated across the exported resources.
*/
async function resolveAndValidateConfigExports(exports$2, ctx) {
	const resolveDefinition = createDefinitionResolver(ctx);
	const parsedResources = /* @__PURE__ */ new Map();
	const resolvedExports = {};
	for (const [name, input] of Object.entries(exports$2)) resolvedExports[name] = await resolveDefinition(input);
	const typeResult = ConfigExportsTypeSchema.safeParse(resolvedExports);
	if (!typeResult.success) return typeResult;
	const issues = [];
	const data = {};
	const resolvedSettings = resolvedExports.settings;
	const settingsResult = resolvedSettings ? InputSettingsSchema.safeParse(resolvedSettings) : void 0;
	if (settingsResult) if (settingsResult.success) data.settings = settingsResult.data;
	else issues.push(...prefixIssues(settingsResult.error.issues, "settings"));
	for (const [name, input] of Object.entries(exports$2)) {
		const resolved = resolvedExports[name];
		if (!isRecord(resolved)) continue;
		if (resolved.type !== "worker" && resolved.type !== "container") continue;
		let result = parsedResources.get(input);
		if (!result) {
			if (resolved.type === "worker") {
				const normalized = await normalizeConfigReferences(resolved, resolveDefinition, resolvedExports);
				issues.push(...prefixIssues(normalized.issues, name));
				result = InputWorkerSchema.safeParse(normalized.value);
			} else result = InputContainerSchema.safeParse(resolved);
			parsedResources.set(input, result);
			if (!result.success) issues.push(...prefixIssues(result.error.issues, name));
		}
		if (result.success) data[name] = result.data;
	}
	issues.push(...validateUniqueResourceNames(data), ...validateContainerExportLinks(data));
	return issues.length > 0 ? {
		success: false,
		error: new z.ZodError(issues)
	} : {
		success: true,
		data
	};
}
/**
* Load a `cloudflare.config.ts`, resolve all exports, and validate them.
*/
async function loadAndValidateConfig(configPath, ctx, options) {
	const { exports: exports$2, dependencies } = await loadConfig(configPath, options);
	return {
		result: await resolveAndValidateConfigExports(exports$2, ctx),
		dependencies
	};
}

//#endregion
export { AnalyticsEngineDatasetBindingSchema, AssetsSchema, BindingSchema, BrowserBindingSchema, D1BindingSchema, DurableObjectCreatedExportSchema, DurableObjectDeletedExportSchema, DurableObjectExpectingTransferExportSchema, DurableObjectRenamedExportSchema, DurableObjectTransferredExportSchema, ExportSchema, FlagshipBindingSchema, HyperdriveBindingSchema, InputContainerSchema, InputSettingsSchema, InputWorkerSchema, KVBindingSchema, KnownBindingSchema, ModuleTypeSchema, OutputContainerSchema, OutputSettingsSchema, OutputWorkerSchema, QueueBindingSchema, R2BindingSchema, TailConsumerSchema, UnsafeBindingSchema, WorkerBindingSchema, WorkerEntrypointExportSchema, bindings, convertToWranglerConfig, defineContainer, defineSettings, defineWorker, exports$1 as exports, generateTypes, loadAndValidateConfig, loadConfig, registerConfigHooks, resolveAndValidateConfigExports, triggers, validateSingletonBindings };